import React, { useEffect, useRef } from 'react';

type AdSlotProps = {
  slotId: string;
  className?: string;
  minHeightPx?: number;
  testMode?: boolean;
};

/**
 * Lightweight AdSense slot that safely injects the AdSense script once,
 * reserves vertical space to avoid CLS, and lazy-loads when near viewport.
 */
const AdSlot: React.FC<AdSlotProps> = ({
  slotId,
  className,
  minHeightPx = 100,
  testMode,
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const insRef = useRef<HTMLModElement | null>(null);
  const hasRequestedRef = useRef<boolean>(false);

  const clientId = process.env.REACT_APP_ADSENSE_CLIENT; // e.g., "ca-pub-XXXXXXXXXXXXXXXX"
  const isTestMode =
    typeof testMode === 'boolean'
      ? testMode
      : process.env.REACT_APP_ADSENSE_TEST_MODE === 'true';

  // Inject AdSense script once per page
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!clientId) return; // no client configured -> do nothing

    const existing = document.querySelector(
      'script[src^="https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?"]'
    ) as HTMLScriptElement | null;

    if (!existing) {
      const script = document.createElement('script');
      script.async = true;
      script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${encodeURIComponent(
        clientId
      )}`;
      (script as any).crossOrigin = 'anonymous';
      document.head.appendChild(script);
    }
  }, [clientId]);

  // Request ad rendering when the slot approaches viewport
  useEffect(() => {
    if (!containerRef.current || !clientId || hasRequestedRef.current) return;
    if (typeof window === 'undefined') return;

    (window as any).adsbygoogle = (window as any).adsbygoogle || [];

    const requestAd = () => {
      try {
        (window as any).adsbygoogle.push({});
        hasRequestedRef.current = true;
      } catch {
        // Swallow; AdSense script might not be ready yet but observer retry is minimal.
      }
    };

    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting && !hasRequestedRef.current) {
            requestAd();
            observer.disconnect();
          }
        });
      },
      { rootMargin: '200px 0px' }
    );

    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [clientId]);

  // If no client configured, render a non-functional placeholder in development
  const shouldRender = Boolean(clientId);

  return (
    <div
      ref={containerRef}
      className={className}
      style={{ minHeight: minHeightPx }}
    >
      {shouldRender ? (
        <ins
          ref={insRef as any}
          className="adsbygoogle"
          style={{ display: 'block' }}
          data-ad-client={clientId}
          data-ad-slot={slotId}
          data-ad-format="auto"
          data-full-width-responsive="true"
          {...(isTestMode ? { 'data-adtest': 'on' } : {})}
        />
      ) : null}
    </div>
  );
};

export default AdSlot;


